import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  input,
  signal,
  TemplateRef,
  viewChild,
  viewChildren,
  ViewContainerRef,
} from '@angular/core';

import { inject } from '@angular/core';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { EmojiSelector } from '@components/emoji-selector/emoji-selector.component';
import { PuebloBase } from '@components/pueblo-base.component';
import {
  MenuDataRegistry,
  PuebloMenu,
  PuebloMenuConfig,
  PuebloMenuContainerBase,
  PuebloMenus,
  PuebloMenusComponentBase,
} from '@services/pueblo-menus.service';

@Component({
  selector: 'pueblo-menu-container',
  imports: [MatMenuModule, NgTemplateOutlet],
  template: `
    <button
      class="trigger-btn"
      [matMenuTriggerFor]="menu"
      [style.left.px]="$targetPos()?.x ?? 0"
      [style.top.px]="$targetPos()?.y ?? 0"
      (menuOpened)="onOpen(); $isOpen.set(true)"
      (menuClosed)="onClose(); $isOpen.set(false)"
      #trigger
    ></button>
    <mat-menu
      #menu="matMenu"
      class="{{ $menuClass() }}"
      [xPosition]="'after'"
      [yPosition]="'below'"
    >
      <ng-content></ng-content>

      @if ($content(); as content) {
        <ng-container *ngTemplateOutlet="content"> </ng-container>
      }
    </mat-menu>
  `,
  styles: `
    mat-menu {
      pointer-events: all;
    }

    .trigger-btn {
      position: fixed;
      width: 0px;
      height: 0px;
      opacity: 0;
    }
  `,
})
export class PuebloMenuContainer implements PuebloMenuContainerBase {
  readonly $type = input.required<PuebloMenu>({ alias: 'type' });
  readonly $content = input<TemplateRef<any>>(undefined, { alias: 'content' });
  readonly $menuClass = input<string | string[]>(undefined, {
    alias: 'menuClass',
  });

  private readonly $trigger = viewChild.required<
    ElementRef<HTMLElement>,
    MatMenuTrigger
  >('trigger', { read: MatMenuTrigger });

  protected readonly $targetPos = signal<{ x: number; y: number } | undefined>(
    undefined,
  );

  readonly $isOpen = signal<boolean>(false);

  protected onOpen: () => void = () => {};
  protected onClose: () => void = () => {};

  open(config: PuebloMenuConfig = {}) {
    if (config.targetPos) {
      this.$targetPos.set(config.targetPos);
    } else if (config.targetElem) {
      const rect = config.targetElem.getBoundingClientRect();
      this.$targetPos.set({
        x: rect.x + rect.width / 2,
        y: rect.y + rect.height / 2,
      });
    } else {
      this.$targetPos.set({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });
    }
    this.onOpen = config.onOpen?.() ?? (() => {});
    this.onClose = config.onClose?.() ?? (() => {});
    setTimeout(() => {
      this.$trigger()?.openMenu();
    }, 10);
  }

  close() {
    this.$trigger()?.closeMenu();
  }
}

// To add a new sheet to this component, start with step 1 in
// services/pueblo-menus.service.ts

// 3. Add the new menu component to the imports array below, then add
//    the menu to the template.
@Component({
  selector: 'pueblo-menus',
  imports: [EmojiSelector, MatMenuModule, PuebloMenuContainer],
  template: `
    <pueblo-menu-container
      class="emoji-menu-container"
      [type]="PuebloMenu.EMOJI_SELECTOR"
      [menuClass]="'emoji-menu'"
      #menuContainer
    >
      @let data = $activeData().get(PuebloMenu.EMOJI_SELECTOR);
      <emoji-selector
        (click)="$event.stopPropagation()"
        (emojiSelected)="data?.emojiSelected?.($event)"
      ></emoji-selector>
    </pueblo-menu-container>

    <ng-container #inlineMenusHost></ng-container>
  `,
  styles: `
    .emoji-menu-container ::ng-deep .mat-mdc-menu-content {
      padding: 0px;
    }
  `,
})
export class PuebloMenusComponent
  extends PuebloBase
  implements PuebloMenusComponentBase
{
  private readonly menus = inject(PuebloMenus);
  private readonly viewContainerRef = inject(ViewContainerRef);

  protected readonly PuebloMenu = PuebloMenu;

  readonly $activeData = signal<Map<PuebloMenu, any>>(
    new Map<PuebloMenu, any>(),
  );

  protected readonly $menuComponents =
    viewChildren<PuebloMenuContainer>('menuContainer');

  protected readonly $inlineMenuHostContainer =
    viewChild.required<ViewContainerRef>('inlineMenusHost');

  ngOnInit() {
    this.menus.init(this);
  }

  open<T extends PuebloMenu>(
    menu: T,
    data?: MenuDataRegistry[T],
    configs?: PuebloMenuConfig,
  ): PuebloMenuContainer {
    const component = this.$menuComponents().find((c) => c.$type() === menu);
    if (!component) {
      throw new Error(`Failed to find menu component for menu type: `);
    }

    const activeData = new Map(this.$activeData());
    activeData.set(menu, data);
    this.$activeData.set(activeData);

    component.open(configs ?? {});
    return component;
  }

  registerInline(
    content: TemplateRef<any>,
    parentDestroyRef: DestroyRef,
  ): PuebloMenuContainer {
    const component = this.viewContainerRef.createComponent(
      PuebloMenuContainer,
      { environmentInjector: this.injector },
    );
    parentDestroyRef.onDestroy(() => {
      component.destroy();
    });
    component.setInput('type', PuebloMenu.INLINE);
    component.setInput('content', content);
    return component.instance;
  }

  close(menu?: PuebloMenu): void {
    this.$menuComponents().forEach((component) => {
      if (
        (menu === undefined || component.$type() === menu) &&
        component.$isOpen()
      ) {
        component.close();
      }
    });
  }
}
